import { useCallback } from "react";

import { useSessionCtx, useSessionJWT } from "utils";
import { logError } from "api/accounts-customer";
import { useOverlaySpinner } from "components/OverlaySpinner";
import { useTOSGate } from "./useTOSGate";

/**
 * useTOSGatedAction
 * @param {object} [options]
 * @param {boolean} [options.enabled=false] - opt in to gating, exactly as `useTOSGate`. Opted out,
 * `run(action)` is a straight call to `action` and no network call is made.
 * @param {string} [options.flowLoc] - flowLoc recorded if the sign fails.
 * @param {Function} [options.onSignError] - replaces the default `logError` hand-off, for hosts
 * that must surface a sign failure somewhere else as well (PayPal postMessages the integrator).
 * @returns {{run: Function, blocked: boolean, busy: boolean, error: Error|null}}
 * @description The gated-CTA protocol, in one place: drop a duplicate click, open the spinner
 * *before* anything awaited so the click is not dead air, answer a blocked click through
 * `ensureNotBlocked`, sign, and only then run the action - taking the spinner down afterwards
 * whatever the action does. Hosts keep presentation (`blocked` for an `isDisabled`, `busy` for a
 * loading state) and the action itself.
 *
 * Shared rather than re-derived per CTA because the ordering is the part that must not drift: sign
 * before any external token is minted or redirect is prepared, and always catch - these callbacks
 * are handed to elements that neither await nor catch, so a bare `await signTOS()` would turn a
 * sign failure into an unhandled rejection that surfaces nowhere.
 */
export const useTOSGatedAction = ({ enabled = false, flowLoc, onSignError } = {}) => {
  const { sessionJWT } = useSessionJWT();
  const { sessionCtx } = useSessionCtx();
  const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner();
  const { blockedByTOS, error, signTOS, signing, ensureNotBlocked } = useTOSGate({
    enabled,
  });

  const run = useCallback(
    async (action) => {
      if (signing) return; // drop the second of two fast clicks

      // Before either await. The actions below open their own spinner, but only once the sign has
      // already resolved, so without this the click has no feedback for the length of a POST - or
      // of the status fetch ensureNotBlocked may have to wait out.
      showOverlaySpinner();
      try {
        // A handler guard rather than an isDisabled: several of these CTAs are a Box or a Select
        // with a raw onClick and no disabled concept. ensureNotBlocked is what keeps a blocked
        // click from reading as a broken control, whichever way it is blocked - see useTOSGate.
        if (!(await ensureNotBlocked())) {
          hideOverlaySpinner();
          return;
        }
        await signTOS();
      } catch (error) {
        hideOverlaySpinner();
        // The user-visible message comes from signError in the consent block
        if (onSignError) {
          await onSignError(error);
        } else {
          await logError(sessionJWT, { sessionCtx, error, flowLoc });
        }
        return;
      }

      // The action owns the spinner from here - both current call sites open their own - but the
      // finally is what guarantees it comes down: the overlay is a Modal with closeOnOverlayClick
      // and closeOnEsc false, so an action that throws, or that simply does not manage it, would
      // otherwise leave the widget frozen behind something the user cannot dismiss. Awaited for
      // the same reason: these callbacks are handed to onClick, which neither awaits nor catches.
      try {
        return await action();
      } catch (error) {
        // Logged under its own flowLoc rather than `flowLoc`, which names the sign: the action
        // failing is not the sign failing. Caught at all because `run` is handed to onClick, which
        // neither awaits nor catches - today's actions toast their own failures, and this is the
        // backstop that keeps a future one from becoming an unhandled rejection.
        await logError(sessionJWT, {
          sessionCtx,
          error,
          flowLoc: `${flowLoc ?? "TOS_GATE"}.ACTION`,
        });
      } finally {
        hideOverlaySpinner();
      }
    },
    [
      signing,
      ensureNotBlocked,
      showOverlaySpinner,
      hideOverlaySpinner,
      signTOS,
      onSignError,
      sessionJWT,
      sessionCtx,
      flowLoc,
    ],
  );

  return { run, blocked: blockedByTOS, busy: signing, error };
};

export default useTOSGatedAction;
